import { BreakpointObserver, BreakpointState } from '@angular/cdk/layout'
import { AsyncPipe, DatePipe } from '@angular/common'
import { HttpClient } from '@angular/common/http'
import { Component, HostListener, inject, OnInit, ViewChild } from '@angular/core'
import { MatIconButton } from '@angular/material/button'
import { MatChip } from '@angular/material/chips'
import { MatDialog } from '@angular/material/dialog'
import { MatIcon } from '@angular/material/icon'
import { MatDrawer, MatDrawerContainer } from '@angular/material/sidenav'
import { MatTooltip } from '@angular/material/tooltip'
import { ActivatedRoute, Router } from '@angular/router'
import { TranslateModule, TranslateService } from '@ngx-translate/core'
import { AngularSplitModule } from 'angular-split'
import { filter, forkJoin, map, mergeMap, of, switchMap, take, tap } from 'rxjs'
import { CallInfoComponent } from '../call-info/call-info.component'
import { CallResourcesContainerComponent } from '../call-resources-container/call-resources-container.component'
import { CallTimelineDialogComponent } from '../call-timeline-dialog/call-timeline-dialog.component'
import { Call, CallsService, CallStatus, MetricType } from '../calls.service'
import { DurationPipe } from '../duration.pipe'
import { ExportDialogComponent, ExportDialogInputData, ExportDialogOutputData } from '../export-dialog/export-dialog.component'
import { LoggerService } from '../logger.service'
import { MetricsService } from '../metrics.service'
import { SpinnerComponent } from '../spinner/spinner.component'
import { ToastService } from '../toast.service'
import { ToolbarComponent } from '../toolbar/toolbar.component'
import { UsersService } from '../users.service'
import { ComponentWithUnsavedChanges } from '../unsaved-changes.guard'
import { CallOutputsContainerComponent } from './call-outputs-container/call-outputs-container.component'
import { ShareCallDialogComponent } from './share-call-dialog/share-call-dialog.component'
import { MeetingHealthIndicatorComponent } from '../meeting-health-indicator/meeting-health-indicator.component'
import { getMeetingHealthItems } from '../meeting-health-indicator/meeting-health.utils'
import { TemplatesService } from '../templates.service'

@Component({
  selector: 'app-call',
  standalone: true,
  imports: [
    AsyncPipe,
    DatePipe,
    SpinnerComponent,
    TranslateModule,
    AngularSplitModule,
    MatDrawer,
    MatDrawerContainer,
    MatIcon,
    MatIconButton,
    MatTooltip,
    CallInfoComponent,
    ToolbarComponent,
    DurationPipe,
    MatChip,
    CallOutputsContainerComponent,
    CallResourcesContainerComponent,
    MeetingHealthIndicatorComponent,
  ],
  templateUrl: './call-page.component.html',
  styleUrl: './call-page.component.scss',
})
export class CallPageComponent implements OnInit, ComponentWithUnsavedChanges {
  router = inject(Router)
  route = inject(ActivatedRoute)
  logger = inject(LoggerService)
  dialog = inject(MatDialog)
  http = inject(HttpClient)
  callsService = inject(CallsService)
  usersService = inject(UsersService)
  toastService = inject(ToastService)
  metricsService = inject(MetricsService)
  breakpointObserver = inject(BreakpointObserver)
  translateService = inject(TranslateService)
  templatesService = inject(TemplatesService)

  @ViewChild(CallResourcesContainerComponent)
  callResourcesContainer: CallResourcesContainerComponent | undefined = undefined

  @HostListener('window:beforeunload', ['$event'])
  onBeforeUnload(event: BeforeUnloadEvent): void {
    if (this.hasUnsavedChanges) {
      event.preventDefault()
    }
  }

  get hasUnsavedChanges(): boolean {
    return this.callResourcesContainer?.hasUnsavedTemplateChanges ?? false
  }

  callId$ = this.route.params.pipe(
    map((params) => (typeof params['id'] === 'string' ? parseInt(params['id'], 10) : null)),
    filter((id) => id !== null)
  )

  call$ = this.callId$.pipe(
    switchMap((id) => this.callsService.getCall(id!)),
    tap((result) => {
      if (result.isSuccess) {
        this.metricsService.sendMetric(result.data.id, MetricType.VIEW_CALL)
      }
    })
  )

  templateItems$ = this.callId$.pipe(
    switchMap((callId) => this.templatesService.getTemplateItems(callId)),
    map((result) => (result.isSuccess ? result.data : []))
  )

  isInfoPanelOpen$ = this.route.queryParams.pipe(map((params) => params['isInfoPanelOpen'] === 'true'))
  editorMinSize = 50
  editorMaxSize = 70
  editorSize = this.editorMinSize
  useVerticalMode = false
  currentUser$ = this.usersService.currentUser$

  getMeetingHealthItems(call: Call, templateItems: any[]) {
    return getMeetingHealthItems(call, templateItems, (key: string, params?: Record<string, unknown>) => this.translateService.instant(key, params))
  }

  canUserEdit(call: Call): boolean {
    if (!this.userHasEditorAccess(call)) {
      return false
    }

    if (call.status === CallStatus.COMPLETED && !call.isPostProcessingComplete) {
      return false
    }

    return true
  }

  userHasEditorAccess(call: Call): boolean {
    return ['FULL_ACCESS', 'EDITOR'].includes(call.accessLevel)
  }

  canUserComment(call: Call): boolean {
    return ['FULL_ACCESS', 'EDITOR', 'COMMENTER'].includes(call.accessLevel)
  }

  // TODO Fix the typing of these methods after correctly transforming dates in the server response on calls.service.ts
  getExpiryText(deletionDate: Date | string | null): string {
    if (deletionDate === null) {
      return this.translateService.instant('CALLS.EXPIRES.NEVER')
    }

    const now = new Date()
    const hoursLeft = Math.max((new Date(deletionDate).getTime() - now.getTime()) / (1000 * 60 * 60), 0)

    if (hoursLeft >= 24) {
      const days = Math.round(hoursLeft / 24)
      const key = days === 1 ? 'CALLS.EXPIRES.DAYS' : 'CALLS.EXPIRES.DAYS_PLURAL'
      return this.translateService.instant(key, { count: days })
    } else if (hoursLeft >= 1.5) {
      const hours = Math.round(hoursLeft)
      const key = hours === 1 ? 'CALLS.EXPIRES.HOURS' : 'CALLS.EXPIRES.HOURS_PLURAL'
      return this.translateService.instant(key, { count: hours })
    } else {
      const minutes = Math.round(hoursLeft * 60)
      const key = minutes === 1 ? 'CALLS.EXPIRES.MINUTES' : 'CALLS.EXPIRES.MINUTES_PLURAL'
      return this.translateService.instant(key, { count: minutes })
    }
  }

  isExpiryUrgent(deletionDate: Date | string | null): boolean {
    if (deletionDate === null) {
      return false
    }

    const now = new Date()
    const hoursLeft = (new Date(deletionDate).getTime() - now.getTime()) / (1000 * 60 * 60)
    return hoursLeft < 24
  }

  getExpiryTooltip(deletionDate: Date | string | null): string {
    if (deletionDate === null) {
      return this.translateService.instant('CALLS.EXPIRES.TOOLTIP_NEVER')
    }

    const expiryDate = new Date(deletionDate)
    const day = expiryDate.getDate()
    const monthNames = [
      this.translateService.instant('COMMON.MONTHS_SHORT.JAN'),
      this.translateService.instant('COMMON.MONTHS_SHORT.FEB'),
      this.translateService.instant('COMMON.MONTHS_SHORT.MAR'),
      this.translateService.instant('COMMON.MONTHS_SHORT.APR'),
      this.translateService.instant('COMMON.MONTHS_SHORT.MAY'),
      this.translateService.instant('COMMON.MONTHS_SHORT.JUN'),
      this.translateService.instant('COMMON.MONTHS_SHORT.JUL'),
      this.translateService.instant('COMMON.MONTHS_SHORT.AUG'),
      this.translateService.instant('COMMON.MONTHS_SHORT.SEP'),
      this.translateService.instant('COMMON.MONTHS_SHORT.OCT'),
      this.translateService.instant('COMMON.MONTHS_SHORT.NOV'),
      this.translateService.instant('COMMON.MONTHS_SHORT.DEC'),
    ]
    const month = monthNames[expiryDate.getMonth()]
    const year = expiryDate.getFullYear()
    const hours = expiryDate.getHours().toString().padStart(2, '0')
    const minutes = expiryDate.getMinutes().toString().padStart(2, '0')

    return this.translateService.instant('CALLS.EXPIRES.TOOLTIP_DATE', {
      day: day,
      month: month,
      year: year,
      hours: hours,
      minutes: minutes,
    })
  }

  ngOnInit() {
    this.trackResponsiveBreakpoints()
  }

  toggleInfoPanel(): void {
    this.route.queryParams
      .pipe(
        map((params) => params['isInfoPanelOpen'] === 'true'),
        take(1)
      )
      .subscribe((isOpen) => {
        this.router.navigate([], {
          relativeTo: this.route,
          queryParams: { isInfoPanelOpen: isOpen ? null : true },
          queryParamsHandling: 'merge',
          skipLocationChange: true,
        })
      })
  }

  exportCall(call: Call): void {
    const dialogRef = this.dialog.open(ExportDialogComponent, {
      height: '365px',
      width: '450px',
      data: {
        allowTranscriptDownload: call.allowTranscriptDownload,
        allowMinutesDownload: call.allowMinutesDownload,
        allowOutlineDownload: call.allowOutlineDownload,
        hasTranscript: call.isTranscriptReady,
        hasOutline: call.isOutlineReady,
        hasActionItems: call.isOutlineReady,
        hasMinutes: call.hasMinutes,
      } as ExportDialogInputData,
    })
    const subscription = dialogRef.componentInstance.exportClicked
      .pipe(
        mergeMap((result: ExportDialogOutputData) => {
          const transcriptExport$ = result.includeTranscript ? this.callsService.exportTranscript(call, result.transcriptFormat) : of(null)
          const minutesExport$ = result.includeMinutes ? this.callsService.exportMinutes(call, result.minutesFormat) : of(null)
          const outlineExport$ = result.includeOutline ? this.callsService.exportOutline(call, result.outlineFormat) : of(null)
          const actionItemsExport$ = result.includeActionItems ? this.callsService.exportActionItems(call, result.actionItemsFormat) : of(null)
          return forkJoin([transcriptExport$, minutesExport$, outlineExport$, actionItemsExport$])
        })
      )
      .subscribe(() => {
        dialogRef.componentInstance.loading = false
        dialogRef.close()
      })
    dialogRef.afterClosed().subscribe(() => {
      subscription.unsubscribe()
    })
  }

  share(call: Call): void {
    this.dialog.open(ShareCallDialogComponent, {
      height: '700px',
      width: '700px',
      data: call,
    })
  }

  showTimeline(call: Call): void {
    this.dialog.open(CallTimelineDialogComponent, {
      width: '900px',
      maxHeight: '80vh',
      data: call,
      panelClass: 'timeline-dialog',
    })
  }

  private trackResponsiveBreakpoints() {
    this.breakpointObserver.observe(['(max-width: 1100px)']).subscribe((result: BreakpointState) => {
      this.useVerticalMode = result.matches
    })
  }

  constructBackButtonLink(call: Call): string[] {
    if (call.accessLevel === 'FULL_ACCESS') {
      return ['/app', 'meetings', 'past']
    }
    return ['/app', 'meetings', 'shared-with-me']
  }
}
